import api from './api';
import { User, CreateUserRequest, CreateUserResponse, PaginatedResponse, UserWorkScheduleEntry } from '../types';
import { API_ENDPOINTS } from '../constants';

// Simple in-memory cache for user data
class UserCacheService {
  private cache = new Map<string, { data: any; timestamp: number; ttl: number }>();

  set(key: string, data: any, ttl: number = 300000) { // 5 minutes default
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl
    });
  }

  get(key: string): any | null {
    const item = this.cache.get(key);
    if (!item) return null;

    const now = Date.now();
    if (now - item.timestamp > item.ttl) {
      this.cache.delete(key);
      return null;
    }

    return item.data;
  }

  clear() {
    this.cache.clear();
  }

  delete(key: string) {
    this.cache.delete(key);
  }

  // Clear all user-related cache
  clearUserCache() {
    const keysToDelete = Array.from(this.cache.keys()).filter(key => 
      key.startsWith('users_') || key.startsWith('user_')
    );
    keysToDelete.forEach(key => this.cache.delete(key));
  }
}

const userCacheService = new UserCacheService();

const userService = {
  /**
   * Get paginated list of users (with caching)
   */
  getUsers: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    role?: string;
    cluster_id?: string;
    store_id?: string;
    is_active?: boolean;
  }): Promise<PaginatedResponse<User>> => {
    const cacheKey = `users_${JSON.stringify(params || {})}`;
    
    // Try to get from cache first
    const cachedData = userCacheService.get(cacheKey);
    if (cachedData) {
      return cachedData;
    }

    const response = await api.get<{ success: boolean; message: string; data: PaginatedResponse<User> }>(API_ENDPOINTS.USERS.LIST, {
      params,
    });
    
    const data = response.data.data;
    
    // Cache the data for 2 minutes
    userCacheService.set(cacheKey, data, 120000);
    
    return data;
  },
  
  /**
   * Get user by ID
   */
  getUserById: async (id: string): Promise<User> => {
    const response = await api.get<{ success: boolean; message: string; data: User }>(API_ENDPOINTS.USERS.GET_BY_ID(id));
    return response.data.data;
  },

  /**
   * Get user profile with full details
   */
  getUserProfile: async (id: string): Promise<User> => {
    const response = await api.get<{ success: boolean; message: string; data: User }>(API_ENDPOINTS.USERS.PROFILE(id));
    return response.data.data;
  },
  
  /**
   * Create new user
   */
  createUser: async (userData: CreateUserRequest): Promise<CreateUserResponse> => {
    const response = await api.post<{ success: boolean; message: string; data: CreateUserResponse }>(
      API_ENDPOINTS.USERS.CREATE,
      userData
    );
    return response.data.data;
  },
  
  /**
   * Update user
   */
  updateUser: async (id: string, userData: Partial<CreateUserRequest>): Promise<User> => {
    const response = await api.put<{ success: boolean; message: string; data: User }>(API_ENDPOINTS.USERS.UPDATE(id), userData);
    return response.data.data;
  },
  
  /**
   * Delete user
   */
  deleteUser: async (id: string): Promise<void> => {
    await api.delete(API_ENDPOINTS.USERS.DELETE(id));
  },
  
  /**
   * Update user status (activate/deactivate)
   */
  updateUserStatus: async (id: string, isActive: boolean): Promise<User> => {
    const response = await api.patch<{ success: boolean; message: string; data: User }>(
      API_ENDPOINTS.USERS.UPDATE_STATUS(id),
      { is_active: isActive }
    );
    return response.data.data;
  },
  
  /**
   * Regenerate PIN for user
   */
  regeneratePIN: async (id: string): Promise<{ user: User; pin: string; message: string }> => {
    const response = await api.post<{ success: boolean; message: string; data: { user: User; pin: string; message: string } }>(
      API_ENDPOINTS.USERS.REGENERATE_PIN(id)
    );
    return response.data.data;
  },
  
  /**
   * Get user attendance
   */
  getUserAttendance: async (id: string, params?: {
    page?: number;
    limit?: number;
    start_date?: string;
    end_date?: string;
  }): Promise<any> => {
    const response = await api.get(API_ENDPOINTS.USERS.ATTENDANCE(id), {
      params,
    });
    return response.data;
  },
  
  /**
   * Get user leaves
   */
  getUserLeaves: async (id: string, params?: {
    page?: number;
    limit?: number;
    status?: string;
    leave_type?: string;
  }): Promise<any> => {
    const response = await api.get(API_ENDPOINTS.USERS.LEAVES(id), {
      params,
    });
    return response.data;
  },
  
  /**
   * Get user documents
   */
  getUserDocuments: async (id: string, params?: {
    page?: number;
    limit?: number;
    document_type?: string;
    status?: string;
  }): Promise<any> => {
    const response = await api.get(API_ENDPOINTS.USERS.DOCUMENTS(id), {
      params,
    });
    return response.data;
  },

  /**
   * Get user weekly work schedule
   */
  getUserWorkSchedule: async (id: string): Promise<{ user_id: string; schedule: UserWorkScheduleEntry[] }> => {
    const response = await api.get<{ success: boolean; message: string; data: { user_id: string; schedule: UserWorkScheduleEntry[] } }>(
      API_ENDPOINTS.USERS.WORK_SCHEDULE(id)
    );
    return response.data.data;
  },

  /**
   * Update user weekly work schedule
   */
  updateUserWorkSchedule: async (id: string, schedule: { day_of_week: number; is_week_off: boolean; shift_id?: string | null }[]) => {
    const response = await api.put<{ success: boolean; message: string; data: { user_id: string; schedule: UserWorkScheduleEntry[] } }>(
      API_ENDPOINTS.USERS.WORK_SCHEDULE(id),
      { schedule }
    );
    return response.data.data;
  },

  /**
   * Clear user cache (call after create/update/delete operations)
   */
  clearCache: () => {
    userCacheService.clearUserCache();
  },

  /**
   * Clear specific user cache
   */
  clearUserCache: (userId: string) => {
    userCacheService.delete(`user_${userId}`);
    userCacheService.delete(`user_profile_${userId}`);
  },
};

export default userService;
